/**
 * Session app actions — the canonical facilitator lifecycle, packaged for the
 * consumer surface. Every call goes through the existing session/tool-session
 * stores and RPCs; nothing here writes outside the canonical paths (FR-5).
 *
 * Sequence (same as the B-Mode FacilitatorControls flow in SessionDetail):
 *   1. openForParticipants — lock config (DRAFT → CRITERIA_FINALIZED) and
 *      create the shared async tool_session participants submit into.
 *   2. closeAndComputeResults — count the answers with the one shared rule
 *      (rankingOutputForToolSession), then hand the ranking to `run_session` (771): ranking_output, the RUN_COMPLETE
 *      state and the session_results rows are one server transaction, so a
 *      refusal leaves nothing behind.
 *   3. publish — publishSessionResults(), the one governed publish lane.
 * Each act re-reads only its own session afterwards.
 */

import {
  publishSessionResults,
  runSessionViaRpc,
  useSessionStore,
} from '@/stores/prioritization/sessionStore';
import { uniqueLines } from '@/lib/uniqueLines';
import {
  rankingOutputForToolSession,
  useToolSessionStore,
} from '@/stores/prioritization/toolSessionStore';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '@/lib/supabaseSync';
import { emitSessionEvent } from '@/lib/sessionEvents';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import {
  legacyModesForRunMode,
  type PrioritizationSession,
  type SessionModeConfig,
} from '@/types/prioritization/session';
import {
  normalizeSessionToolId,
  resolveSessionToolDescriptor,
  SESSION_TOOL_DESCRIPTORS,
  sessionHasObviousOrLockedCriteria,
} from '@/lib/sessionToolCatalog';
import {
  getOutputTypeForSessionTool,
  resolveToolRegistrySlug,
  type ToolOutputType,
} from '@/lib/toolRegistry';
import { classificationCategoryOrder, QUADRANT_LABELS } from '@/lib/liveSessionDisplay';
import { WEIGHTED_SCORING_DEFAULT_CRITERIA, type ScoringCriterion } from '@/lib/sessionScoringCriteria';
import { pickDefaultMethod, recommendMethods } from '@/lib/sessions/recommendMethods';
import { pooledLine } from '@/lib/buyAFeaturePricing';
import { isToolSwitchedOff } from '@/lib/switchedOffTools';

/**
 * The methods the Session app offers come from the platform's method catalog
 * (SESSION_TOOL_DESCRIPTORS, built from the registry) — never a list kept by
 * hand here. Names come from the same catalog everywhere in the app.
 *
 * Left out, on purpose: Speed Boat and Fast vs Right. They do not rank a list
 * of options — Speed Boat sorts what speeds a team up or holds it back, and
 * Fast vs Right classifies the decision itself. Both stay in the full product.
 */
export const SESSION_APP_LEFT_OUT_METHODS: readonly string[] = ['speed_boat', 'fast_vs_right'];

/**
 * How a session made in the Session app (and Rank and Allocate, which share its
 * host console and join address) runs: its people answer in the product's live
 * room (ruling R1, 2026-10-21; the host's Open the room), as a session from a
 * track's Run it live does. Said as a run mode with its legacy pair, so the
 * run_mode column and mode_config agree (migration 1045). These apps used to
 * send only `timingMode: 'async'`, which the browser read as facilitator_live
 * and the column as open.
 *
 * An offline blueprint (Hire, Vendor Select, Risk Triage) is different: it has
 * an answer window, which the server closes on timing 'async' (migration 991),
 * so it is `open`; its typed options keep it in the live room too
 * (asyncRoomCanShowOptions).
 */
export const SESSION_APP_MODE_CONFIG: Pick<SessionModeConfig, 'runMode' | 'timingMode' | 'entryMode'> = {
  runMode: 'facilitator_live',
  ...legacyModesForRunMode('facilitator_live'),
};

/**
 * Criteria a method uses when it needs criteria declared before Open and the
 * app has nowhere to pick them. Weighted scoring gets its own built-in set;
 * the host is told on the setup screen and can change them in the full product.
 */
const SESSION_APP_DEFAULT_CRITERIA: Record<string, ScoringCriterion[]> = {
  simple_weighted_scoring: WEIGHTED_SCORING_DEFAULT_CRITERIA,
};

export type SessionAppMethodGroup = 'vote_or_rank' | 'score' | 'sort';

/** Plain-word groups for the "More methods" list, in the order shown. */
export const SESSION_APP_METHOD_GROUPS: ReadonlyArray<{ group: SessionAppMethodGroup; label: string }> = [
  { group: 'vote_or_rank', label: 'Vote or rank' },
  { group: 'score', label: 'Score' },
  { group: 'sort', label: 'Sort into groups' },
];

/** A method's group follows from how its answers are counted (the one counting rule). */
const GROUP_BY_OUTPUT: Record<ToolOutputType, SessionAppMethodGroup> = {
  ranking: 'vote_or_rank',
  scoring: 'score',
  classification: 'sort',
};

/**
 * One-line plain blurbs, keyed by catalog id. Copy only — which methods exist
 * comes from the catalog; a method without a line here shows the catalog's own
 * description.
 */
const SESSION_APP_METHOD_BLURBS: Record<string, string> = {
  dot_voting: 'Everyone gets a handful of dots to spread across the options. Fast and energizing.',
  stack_ranking: 'Each person orders the full list. Best when everyone knows all the options.',
  pairwise_ranking: 'Compare two options at a time. Slower, but great when the list is contested.',
  max_priorities_pyramid: 'Narrow the list step by step until only the top few are left.',
  magic_prioritization: 'Judge urgency, value and size one at a time, then see what comes first.',
  buy_a_feature: 'Each person gets a budget and spends it on the options they want most.',
  rice: 'Score each option on reach, impact, confidence and effort.',
  wsjf: 'Score the cost of waiting against the size of the job.',
  cost_of_delay: 'Score what is lost for each option that waits.',
  simple_weighted_scoring: 'Score each option on a few weighted criteria.',
  ahp: 'Compare options two at a time on each criterion for a careful score.',
  moscow: 'Put each option in Must, Should, Could or Won’t.',
  impact_effort_matrix: 'Place each option by how much it helps and how hard it is.',
  eisenhower_matrix: 'Sort each option by how urgent and how important it is.',
  pareto_analysis: 'Find the few options that give most of the value.',
  prune_product_tree: 'Rate each option, then decide what to keep and what to cut.',
};

export interface SessionAppMethod {
  /** Stored as the session's selectedToolId — the registry slug, like every App. */
  id: string;
  /** The platform catalog id (registry id). */
  catalogId: string;
  name: string;
  blurb: string;
  group: SessionAppMethodGroup;
  /** Set when the method needs criteria declared before Open; the app declares these. */
  defaultCriteria?: ScoringCriterion[];
}

/** Would Open refuse a fresh app session with this method for want of criteria? */
function needsDeclaredCriteria(catalogId: string): boolean {
  return !sessionHasObviousOrLockedCriteria({
    selectedToolId: catalogId,
    criteriaConfig: { criteriaIds: [], weights: {}, reconciliationRequiredSet: [] },
    trackId: null,
    state: 'DRAFT',
  });
}

export const SESSION_APP_METHODS: SessionAppMethod[] = SESSION_TOOL_DESCRIPTORS.flatMap((descriptor) => {
  const catalogId = descriptor.compatibilityId;
  if (SESSION_APP_LEFT_OUT_METHODS.includes(catalogId)) return [];
  // A method switched off in code (AHP, owner rulings 2026-10-09 and 10-28) is
  // offered nowhere, so not here either; the app used to list it anyway.
  if (isToolSwitchedOff(catalogId)) return [];
  // No registry entry means nothing on the platform can count its answers.
  const slug = resolveToolRegistrySlug(catalogId);
  if (!slug) return [];
  const needsCriteria = needsDeclaredCriteria(catalogId);
  const defaultCriteria = SESSION_APP_DEFAULT_CRITERIA[catalogId];
  // A method Open would refuse, with no built-in set to declare, is not offered.
  if (needsCriteria && !defaultCriteria) return [];
  return [{
    id: slug,
    catalogId,
    name: descriptor.name,
    blurb: SESSION_APP_METHOD_BLURBS[catalogId] ?? descriptor.description,
    group: GROUP_BY_OUTPUT[getOutputTypeForSessionTool(catalogId)],
    ...(needsCriteria ? { defaultCriteria } : {}),
  }];
});

/** The app's method for any form of a tool id (slug, registry id or alias). */
export function sessionAppMethod(toolId: string | null | undefined): SessionAppMethod | undefined {
  const catalogId = normalizeSessionToolId(toolId);
  return catalogId ? SESSION_APP_METHODS.find((m) => m.catalogId === catalogId) : undefined;
}

/** The method's name from the platform catalog, for any form of its id. */
export function sessionAppToolName(toolId: string | null | undefined): string {
  return resolveSessionToolDescriptor(toolId)?.name ?? toolId ?? 'No method chosen';
}

/** One method on the setup screen, as the shared recommender sees it for this many options. */
export interface SessionAppMethodChoice extends SessionAppMethod {
  /** The recommender leads with it and it fits the option count. */
  recommended: boolean;
  /** Why it does not fit the option count, in plain words, when it does not. */
  fitNote?: string;
}

export interface SessionAppMethodSections {
  /** The method the setup leads with: the recommender's default among the app's methods. */
  recommended: SessionAppMethodChoice;
  /** Every recommended method that fits, the default first. */
  first: SessionAppMethodChoice[];
  /** Everything else, in plain-word groups and the recommender's order; empty groups left out. */
  more: Array<{ group: SessionAppMethodGroup; label: string; methods: SessionAppMethodChoice[] }>;
}

/**
 * How the setup screen lays out the methods for this many options. The order
 * and the default come from the one recommender (`recommendMethods`), the same
 * one a track's Run it live uses, so the same list gets the same advice.
 */
export function sessionAppMethodSections(optionCount: number): SessionAppMethodSections {
  // Below two options nothing fits; recommend for the smallest list that can run
  // rather than mark every method "needs at least 2 items" while the host types.
  const { choices } = recommendMethods({ itemCount: Math.max(optionCount, 2) });
  const offered = new Set(SESSION_APP_METHODS.map((m) => m.catalogId));
  const ours = choices.filter((c) => offered.has(c.toolId));
  const position = new Map(ours.map((c, i) => [c.toolId, i]));
  const all: SessionAppMethodChoice[] = SESSION_APP_METHODS.map((method) => {
    const choice = ours.find((c) => c.toolId === method.catalogId);
    return {
      ...method,
      recommended: choice?.recommended === true && choice.fits,
      ...(choice && !choice.fits && choice.fitNote ? { fitNote: choice.fitNote } : {}),
    };
  }).sort(
    (a, b) =>
      (position.get(a.catalogId) ?? Number.MAX_SAFE_INTEGER) - (position.get(b.catalogId) ?? Number.MAX_SAFE_INTEGER),
  );
  const defaultId = pickDefaultMethod(ours)?.toolId;
  const recommended = all.find((m) => m.catalogId === defaultId) ?? all[0];
  const first = [recommended, ...all.filter((m) => m.recommended && m !== recommended)];
  const shown = new Set(first.map((m) => m.catalogId));
  const more = SESSION_APP_METHOD_GROUPS.map(({ group, label }) => ({
    group,
    label,
    methods: all.filter((m) => m.group === group && !shown.has(m.catalogId)),
  })).filter((section) => section.methods.length > 0);
  return { recommended, first, more };
}

/** The setup line for a method that runs on built-in criteria, or null. */
export function sessionAppDefaultCriteriaLine(method: SessionAppMethod | undefined): string | null {
  if (!method?.defaultCriteria?.length) return null;
  const names = method.defaultCriteria.map((c) => c.name);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return `Scores on ${list}, weighted equally. You can change the criteria in the full product.`;
}

export interface SessionAppResultRow {
  id: string;
  name: string;
  /** The group (sorting methods) or the score (score methods); none for vote/rank. */
  detail?: string;
}

function formatScore(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/**
 * The host's result list: the saved order, plus each option's group (sorting
 * methods, with how many agreed) or its average score (score methods).
 */
export function sessionAppResultRows(
  session: Pick<PrioritizationSession, 'selectedToolId' | 'rankingOutput'>,
  items: Array<{ id: string; name: string }>,
  /** Buy-a-Feature: each item's price, so the row says what the room pledged against it. */
  prices?: Readonly<Record<string, number>> | null,
): SessionAppResultRow[] {
  const output = session.rankingOutput;
  if (!output?.rankedItemIds?.length) return [];
  const toolId = normalizeSessionToolId(session.selectedToolId);
  const outputType = getOutputTypeForSessionTool(toolId);
  const classifications = (output.classifications ?? {}) as Record<string, unknown>;
  const scores = (output.scores ?? {}) as Record<string, unknown>;
  return output.rankedItemIds.map((id) => {
    const name = items.find((i) => i.id === id)?.name ?? id;
    let detail: string | undefined;
    if (outputType === 'classification') {
      const c = classifications[id] as { primaryCategory?: unknown; agreement?: unknown } | undefined;
      const category = typeof c?.primaryCategory === 'string' ? c.primaryCategory : undefined;
      if (category) {
        const label = QUADRANT_LABELS[category] ?? category;
        detail = typeof c?.agreement === 'number' ? `${label} · ${c.agreement}% agree` : label;
      }
    } else if (outputType === 'scoring') {
      const total = (scores[id] as { total?: unknown } | undefined)?.total;
      if (typeof total === 'number' && Number.isFinite(total)) detail = `${formatScore(total)} avg`;
    } else if (toolId === 'buy_a_feature' && prices?.[id] != null) {
      // Pooled: everyone's pledges add up; the item is bought once they reach its price.
      const total = (scores[id] as { total?: unknown } | undefined)?.total;
      detail = pooledLine(typeof total === 'number' && Number.isFinite(total) ? Math.round(total) : 0, prices[id]);
    }
    return detail ? { id, name, detail } : { id, name };
  });
}

export interface SessionAppResultGroup {
  /** The group as the method stores it (`Must`, `quick-wins`, `ui`…), or `unplaced`. */
  key: string;
  label: string;
  rows: Array<{
    id: string;
    name: string;
    /** Share of the people who sorted it that chose this group. */
    agreement?: number;
    /** How everyone sorted it, largest group first, when they did not all agree. */
    split?: string;
  }>;
}

/**
 * A sorting method's result as the groups themselves: each group in the
 * method's order, the options in it in the saved order, with how many agreed
 * and how the rest sorted it. Null for methods that do not sort into groups.
 */
export function sessionAppResultGroups(
  session: Pick<PrioritizationSession, 'selectedToolId' | 'rankingOutput'>,
  items: Array<{ id: string; name: string }>,
): SessionAppResultGroup[] | null {
  const output = session.rankingOutput;
  if (!output?.rankedItemIds?.length) return null;
  if (getOutputTypeForSessionTool(normalizeSessionToolId(session.selectedToolId)) !== 'classification') return null;
  const classifications = (output.classifications ?? {}) as Record<string, unknown>;
  const label = (key: string) => QUADRANT_LABELS[key] ?? key;
  const groups = new Map<string, SessionAppResultGroup>();
  for (const id of output.rankedItemIds) {
    const c = classifications[id] as
      | { primaryCategory?: unknown; agreement?: unknown; breakdown?: unknown }
      | undefined;
    const key = typeof c?.primaryCategory === 'string' ? c.primaryCategory : 'unplaced';
    if (!groups.has(key)) {
      groups.set(key, { key, label: key === 'unplaced' ? 'Not placed' : label(key), rows: [] });
    }
    const breakdown = Object.entries((c?.breakdown ?? {}) as Record<string, unknown>)
      .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > 0)
      .sort((a, b) => b[1] - a[1]);
    groups.get(key)!.rows.push({
      id,
      name: items.find((i) => i.id === id)?.name ?? id,
      ...(typeof c?.agreement === 'number' ? { agreement: c.agreement } : {}),
      ...(breakdown.length > 1 ? { split: breakdown.map(([k, n]) => `${n} ${label(k)}`).join(' · ') } : {}),
    });
  }
  return [...groups.values()].sort(
    (a, b) =>
      (a.key === 'unplaced' ? 1 : 0) - (b.key === 'unplaced' ? 1 : 0) ||
      classificationCategoryOrder(a.key) - classificationCategoryOrder(b.key),
  );
}

/**
 * The result as rows to take out (roadmap S-6): the place, the option and what
 * the method says about it — the same words the host's result list shows.
 */
export function sessionAppResultCsv(
  session: Pick<PrioritizationSession, 'selectedToolId' | 'rankingOutput'>,
  items: Array<{ id: string; name: string }>,
  prices?: Readonly<Record<string, number>> | null,
): Array<Record<string, string | number>> {
  return sessionAppResultRows(session, items, prices).map((row, i) => ({
    Place: i + 1,
    Option: row.name,
    Result: row.detail ?? '',
  }));
}

/** Tool items for a session: workspace items (app_linked) or free-form labels (isolated). */
export function sessionToolItems(session: PrioritizationSession): Array<{ id: string; name: string }> {
  if (session.sessionContextMode === 'isolated') {
    return uniqueLines(session.isolatedItems ?? []).map((text) => ({ id: text, name: text }));
  }
  const { getItemById } = useItemsStore.getState();
  return (session.itemIds ?? []).map((id) => ({
    id,
    name: getItemById(id)?.title ?? id,
  }));
}

export async function openForParticipants(
  session: PrioritizationSession,
  userId: string,
  userName: string
): Promise<{ success: boolean; error?: string }> {
  const sessionStore = useSessionStore.getState();
  if (session.state === 'DRAFT') {
    const result = await sessionStore.transitionSessionState(
      session.id,
      'CRITERIA_FINALIZED',
      session.facilitatorId,
      { participantCount: session.participantIds?.length ?? 0 }
    );
    if (!result.success) {
      return { success: false, error: result.error ?? 'Could not lock the session configuration.' };
    }
  }

  const workspaceId = session.workspaceId ?? getCurrentWorkspaceId() ?? '';
  if (!workspaceId) {
    return { success: false, error: 'No workspace found — open the app from inside a workspace.' };
  }

  try {
    // Prefer live session_participants over the in-memory snapshot — people who
    // joined the waiting room via the share link may have enrolled since load.
    const supabase = getSafeSupabaseClient();
    let participantIds = session.participantIds ?? [];
    if (supabase) {
      const { data: spRows } = await supabase
        .from('session_participants')
        .select('user_id')
        .eq('session_id', session.id)
        .eq('status', 'active');
      if (spRows && spRows.length > 0) {
        participantIds = [...new Set(spRows.map((r: { user_id: string }) => r.user_id))];
      }
    }

    const toolSessionId = await useToolSessionStore.getState().createToolSession({
      sessionId: session.id,
      workspaceId,
      toolType: session.selectedToolId ?? 'dot-voting',
      toolName: sessionAppToolName(session.selectedToolId ?? 'dot-voting'),
      // How the answers are counted follows the method (the one counting rule).
      outputType: getOutputTypeForSessionTool(normalizeSessionToolId(session.selectedToolId ?? 'dot-voting')),
      mode: 'async',
      participantIds,
      participantNames: {},
      facilitatorId: userId,
      facilitatorName: userName,
      items: sessionToolItems(session),
      // An offline session's window (S-9): its activity takes answers until then.
      ...(session.modeConfig?.closesAt ? { deadline: new Date(session.modeConfig.closesAt).toISOString() } : {}),
    });
    // Gate open: participants waiting on the link advance out of the waiting room.
    void emitSessionEvent(session.id, userId, userName, 'waiting_room_started', { toolSessionId });
    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to open the session.',
    };
  }
}

/** Latest tool session id for a session, from DB truth (survives refresh). */
export async function findActiveToolSessionId(sessionId: string): Promise<string | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;
  const { data } = await supabase
    .from('tool_sessions')
    .select('id')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data ? (data as { id: string }).id : null;
}

export async function closeAndComputeResults(
  session: PrioritizationSession
): Promise<{ success: boolean; error?: string }> {
  if (!getSafeSupabaseClient()) return { success: false, error: 'Not connected.' };

  const toolSessionId = await findActiveToolSessionId(session.id);
  if (toolSessionId) {
    // The one counting rule (rankingOutputForToolSession): the same answers give
    // the same order here as on the full session page and the live board.
    const toolId = session.selectedToolId ?? 'dot-voting';
    const rankingOutput = await rankingOutputForToolSession(toolSessionId, {
      toolId,
      toolName: sessionAppToolName(toolId),
    });
    if (!rankingOutput) return { success: false, error: 'No responses submitted yet.' };
    // One act, one write path (771): run_session writes ranking_output, the
    // RUN_COMPLETE state and the session_results rows in one transaction.
    const runResult = await runSessionViaRpc(session.id, rankingOutput);
    if (!runResult.success) {
      return { success: false, error: runResult.error ?? 'Could not save results.' };
    }
    // Re-read this session only: reloading the workspace's every session (525
    // on staging) made each Close take seconds.
    await useSessionStore.getState().fetchSessionById(session.id);
    return { success: true };
  }

  const transition = await useSessionStore
    .getState()
    .transitionSessionState(session.id, 'RUN_COMPLETE', session.facilitatorId, {
      participantCount: session.participantIds?.length ?? 0,
    });
  if (!transition.success) {
    return { success: false, error: transition.error ?? 'Could not close the session.' };
  }
  await useSessionStore.getState().fetchSessionById(session.id);
  return { success: true };
}

export async function publishResults(sessionId: string): Promise<{ success: boolean; error?: string }> {
  // One lane (739): the same records as every other "Publish results" button.
  const result = await publishSessionResults(sessionId);
  if (!result.success) {
    // Cancelling the shared confirmation is not a failure to report.
    if (result.code === 'cancelled') return { success: false };
    return { success: false, error: result.error };
  }
  // publishSessionResults has already re-read this session.
  return { success: true };
}
